import { lazy, Suspense, Component, type ReactNode } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import ScrollToTop from "@/components/ScrollToTop";
import { LanguageProvider } from "@/hooks/useLanguage";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { LANGUAGES } from "@/lib/routes";
import { routePaths } from "@/lib/routes";

const FloatingCTA = lazy(() => import("@/components/mobile/FloatingCTA"));

/** Legacy redirect for /produkte/:type/:category → /de/produkte/:type/:category */
const ProdukteLegacyRedirect = () => {
  const { type, category } = useParams();
  return <Navigate to={`/de/produkte/${type}/${category}`} replace />;
};

/** Legacy redirect for /hersteller/:slug → /de/hersteller/:slug */
const HerstellerLegacyRedirect = () => {
  const { slug } = useParams();
  return <Navigate to={`/de/hersteller/${slug}`} replace />;
};

const TiltInitializer = lazy(() => import("@/components/TiltInitializer"));
const AtmosphereLayer = lazy(() => import("@/components/atmosphere/AtmosphereLayer"));
const LightCanvas = lazy(() => import("@/components/canvas/LightCanvas"));

const HomePage = lazy(() => import("./pages/HomePage"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const ProductsPage = lazy(() => import("./pages/ProductsPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const ManufacturersPage = lazy(() => import("./pages/ManufacturersPage"));
const ManufacturerDetailPage = lazy(() => import("./pages/ManufacturerDetailPage"));
const CategoryPage = lazy(() => import("./pages/CategoryPage"));
const NotFound = lazy(() => import("./pages/NotFound"));
const ImpressumPage = lazy(() => import("./pages/ImpressumPage"));
const DatenschutzPage = lazy(() => import("./pages/DatenschutzPage"));
const CookiePolicyPage = lazy(() => import("./pages/CookiePolicyPage"));
const TermsPage = lazy(() => import("./pages/TermsPage"));

/** Catches chunk-load failures so they don't blank the entire app */
class ChunkErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean }> {
  state = { hasError: false };
  static getDerivedStateFromError() { return { hasError: true }; }
  render() { return this.state.hasError ? null : this.props.children; }
}

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <BrowserRouter>
        <LanguageProvider>
          <Toaster />
          <Sonner />

          {/* Experience Layer — wrapped in error boundary so chunk-load failures don't blank the app */}
          <ChunkErrorBoundary>
            <Suspense fallback={null}>
              <LightCanvas />
              <AtmosphereLayer />
              <TiltInitializer />
            </Suspense>
          </ChunkErrorBoundary>

          <ScrollToTop />
          <Navbar />

          <main className="relative z-10 min-h-screen">
            <Suspense fallback={null}>
              <Routes>
                {/* Root redirects to detected language (handled by LanguageProvider) */}
                <Route path="/" element={null} />

                {/* Generate routes for each language */}
                {LANGUAGES.map((lang) => (
                  <Route key={lang} path={`/${lang}`}>
                    <Route index element={<HomePage />} />
                    <Route path={routePaths.about[lang]} element={<AboutPage />} />
                    <Route path={routePaths.products[lang]} element={<ProductsPage />} />
                    <Route path={routePaths.contact[lang]} element={<ContactPage />} />
                    <Route path={routePaths.manufacturers[lang]} element={<ManufacturersPage />} />
                    <Route path={`${routePaths.manufacturers[lang]}/:slug`} element={<ManufacturerDetailPage />} />
                    <Route path={`${routePaths.productsPrefix[lang]}/:type/:category`} element={<CategoryPage />} />
                    <Route path={routePaths.impressum[lang]} element={<ImpressumPage />} />
                    <Route path={routePaths.datenschutz[lang]} element={<DatenschutzPage />} />
                    <Route path={routePaths.cookiePolicy[lang]} element={<CookiePolicyPage />} />
                    <Route path={routePaths.terms[lang]} element={<TermsPage />} />
                  </Route>
                ))}

                {/* Legacy routes → redirect to German */}
                <Route path="/about" element={<Navigate to="/de/agentur" replace />} />
                <Route path="/products" element={<Navigate to="/de/sortiment" replace />} />
                <Route path="/contact" element={<Navigate to="/de/kontakt" replace />} />
                <Route path="/hersteller" element={<Navigate to="/de/hersteller" replace />} />
                <Route path="/hersteller/:slug" element={<HerstellerLegacyRedirect />} />
                <Route path="/impressum" element={<Navigate to="/de/impressum" replace />} />
                <Route path="/datenschutz" element={<Navigate to="/de/datenschutz" replace />} />
                <Route path="/cookie-richtlinie" element={<Navigate to="/de/cookie-richtlinie" replace />} />
                <Route path="/nutzungsbedingungen" element={<Navigate to="/de/nutzungsbedingungen" replace />} />
                <Route path="/produkte/:type/:category" element={<ProdukteLegacyRedirect />} />

                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </main>

          <Footer />
          <Suspense fallback={null}>
            <FloatingCTA />
          </Suspense>
        </LanguageProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
